import React, { useRef, useMemo, useCallback } from 'react';
import { Field } from '@atlaskit/form';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import PlatformPicker from '@atlassian/jira-project-picker/src/index.tsx';
import type { PickedProject } from '@atlassian/jira-project-picker/src/model/index.tsx';
import type { FieldConfigurationComponentProps } from '../../../../types.tsx';
import type { AssociatedField } from '../../../../types/layout-items.tsx';
import { AkFieldWrapper, FieldConfigurationDefaultValue } from '../../styled.tsx';
import messages from './messages.tsx';

export type Props = {
	readonly field: AssociatedField;
	readonly onUpdate: (arg1: AssociatedField) => void;
	tooltipDelay?: number;
	testId?: string;
};

const onDefaultValueChange =
	(field: AssociatedField, onUpdate: (arg1: AssociatedField) => void) => (defaultValue?: string) =>
		onUpdate({
			...field,
			defaultValue: defaultValue == null || defaultValue === '' ? null : defaultValue,
		});

const ProjectPickerFieldConfiguration = ({
	field,
	onUpdate,
	tooltipDelay,
	testId,
}: FieldConfigurationComponentProps) => {
	const { formatMessage } = useIntl();
	const isModifyDefaultValueDisabled = !field.operations.canModifyDefaultValue;
	const cachedProject = useRef<unknown>();
	const handleChange = useCallback(
		(project: PickedProject) => {
			// This callback fired on mount with an undefined project when no default value is provided. This check
			// prevents the onDefaultValueChange handler being fired unnecessarily when both values are nullish.
			const isNullishValue = project?.value == null && field.defaultValue == null;
			if (!isNullishValue && project?.value !== field.defaultValue) {
				onDefaultValueChange(field, onUpdate)(project && project.value);
			}
			cachedProject.current = project;
		},
		[field, onUpdate],
	);
	const selectedProject = useMemo(
		() =>
			cachedProject.current !== undefined &&
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			field.defaultValue === cachedProject.current.value
				? {
						value: field.defaultValue,
						// @ts-expect-error - TS2571 - Object is of type 'unknown'.
						name: cachedProject.current.name,
						// @ts-expect-error - TS2571 - Object is of type 'unknown'.
						avatarUrl: cachedProject.current.avatarUrl,
					}
				: {
						value: field.defaultValue,
						name: '',
						avatarUrl: '',
					},
		[field],
	);

	return (
		<FieldConfigurationDefaultValue>
			<AkFieldWrapper>
				<Tooltip
					position="bottom"
					content={
						isModifyDefaultValueDisabled
							? formatMessage(messages.projectDefaultValueDisabledTooltip)
							: null
					}
					delay={tooltipDelay}
				>
					<Field
						name={`${field.key}-default-value`}
						label={formatMessage(messages.projectDefaultValueLabel)}
						testId={testId}
					>
						{({ fieldProps }) => (
							<PlatformPicker
								inputId={fieldProps.id}
								baseUrl=""
								// @ts-expect-error - Type '{ value: unknown; name: any; avatarUrl: any; }' is not assignable to type 'Project'.
								selectedProject={selectedProject}
								width="100%"
								requireBrowsePermission
								isDisabled={isModifyDefaultValueDisabled}
								// @ts-expect-error - Type '(project: PickedProject) => void' is not assignable to type '(arg1: Project | undefined, args_1: UIAnalyticsEvent) => void'.
								onChange={handleChange}
							/>
						)}
					</Field>
				</Tooltip>
			</AkFieldWrapper>
		</FieldConfigurationDefaultValue>
	);
};

export default ProjectPickerFieldConfiguration;
